import { useMemo } from 'react'
import { MULTIPLAYER_UI, type RoomInfo } from '../config/multiplayer'
import { leaveRoom } from '../services/rooms'
import bg from '../assets/bg2.webp'

interface Props {
  room: RoomInfo
  /** 自分の userId（相手の名前を出すのに使う。現状は表示上の識別のみ）。 */
  currentUserId: string | null
  onExit: () => void
  /**
   * T15 で実装される対戦開始ハンドラ。
   * 本タスクでは未接続（ダミー）。呼び出し側が用意しない場合は disabled で描画する。
   *
   * T15 実装時の接続ポイント:
   *   - このハンドラ内で `POST /games/:gameId/rooms/:roomId/ws-ticket` を呼び、
   *     `wss://api.yamanekogames.com/ws?token=<TICKET>&roomId=<ROOM_ID>` に接続する。
   *   - 返ってきた WebSocket 上で player_joined / room_command / room_command_result
   *     を購読して `submitCommand({ commandType: 'place_stone', ... })` を送信する。
   *   - 参考: 30_Features/SDK_Reference_Multiplayer.md の connectWebSocket / submitCommand。
   */
  onBattleStart?: () => void
}

/**
 * マッチ成立 or フレンドマッチ参加後に遷移するロビー画面（スタブ）。
 *
 * T14 の責務:
 *   - ルーム情報と参加メンバーの表示
 *   - 退出（leaveRoom → onExit）の導線
 *   - 「対戦開始」ボタン（ホスト想定）のスタブ表示
 *
 * T15 の責務（このファイルに上乗せされる想定）:
 *   - WebSocket 接続の初期化
 *   - 相手の ready 状態の同期
 *   - 対戦開始時のサーバー同期（ホストのみが start を送出）
 */
export function MultiplayerLobby({ room, currentUserId, onExit, onBattleStart }: Props) {
  const opponent = useMemo(() => {
    const me = currentUserId
    return room.members.find((m) => m.userId !== me) ?? null
  }, [room.members, currentUserId])

  const isHost = currentUserId !== null && room.hostUserId === currentUserId

  const handleExit = async () => {
    try {
      await leaveRoom(room.roomId)
    } catch (err) {
      console.warn('[MultiplayerLobby] leaveRoom failed:', err)
    }
    onExit()
  }

  const handleStart = () => {
    if (!onBattleStart) return
    onBattleStart()
  }

  // 対戦開始ボタンはホストのみ押せる。相手入室前に押しても成立しないため、
  // 相手が未着席の間は disabled。対戦開始後は MultiplayerGameScreen 側で
  // 「StartMatch」コマンドを送信し、両クライアントの snapshot を初期化する。
  const canStart = isHost && opponent !== null && typeof onBattleStart === 'function'

  return (
    <div
      className="screen screen--title"
      style={{ backgroundImage: `url(${bg})` }}
    >
      <div className="title-overlay">
        <div className="title-overlay__logo">
          <h1
            style={{
              fontSize: 28,
              fontWeight: 900,
              color: '#ffe066',
              textShadow: '0 2px 8px rgba(0,0,0,0.5)',
            }}
          >
            {MULTIPLAYER_UI.lobbyTitle}
          </h1>
        </div>

        <div className="title-overlay__player">
          <div
            style={{
              padding: 16,
              background: 'rgba(0,0,0,0.35)',
              borderRadius: 12,
              minWidth: 240,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }}>
              ルーム: <code>{room.roomId.slice(0, 10)}…</code>
            </p>
            <p style={{ fontSize: 14 }}>
              参加者: {room.members.length} / {room.capacity}
            </p>
            <p style={{ fontSize: 14 }}>
              あなた: {isHost ? 'ホスト' : 'ゲスト'}
            </p>
            <p style={{ fontSize: 14 }}>
              {opponent
                ? `相手: ${opponent.userId.slice(0, 10)}…`
                : MULTIPLAYER_UI.lobbyOpponentMissing}
            </p>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
              {MULTIPLAYER_UI.lobbyWaiting}
            </p>
          </div>
        </div>

        <div className="title-overlay__actions">
          <button
            type="button"
            className="btn btn--primary btn--large"
            onClick={handleStart}
            disabled={!canStart}
          >
            {MULTIPLAYER_UI.lobbyStartButton}
          </button>
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => {
              void handleExit()
            }}
          >
            {MULTIPLAYER_UI.lobbyLeaveButton}
          </button>
        </div>
      </div>
    </div>
  )
}
